import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Container,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow as StartIcon,
  Schedule as DelayIcon,
  Stop as EndIcon,
  Casino as CasinoIcon,
} from "@mui/icons-material";
import ManageGameModal from "./Modals/ManageGameModal.jsx";

import RequestsSection from "./RequestsSection";
import WaitlistSection from "./WaitlistSection";
import CurrentPlayersSection from "./CurrentPlayersSection";
import AllowedPlayersSection from "./AllowedPlayersSection";

interface RootState {
  host: {
    gamelist: {
      [key: string]: {
        id: string;
        gameName: string;
        status: string;
        minPlayers: number;
        maxPlayers: number;
        waitlist: {
          [key: string]: any;
        };
        currentPlayers: {
          [key: string]: any;
        };
        gameSessionId?: string;
        hostId: string;
        access: string;
        accesscode?: string;
        allowedPlayers?: {
          [key: string]: boolean;
        };
      };
    };
  };
}

function ManageGame() {
  const gamelist = useSelector((state: RootState) => state.host.gamelist);
  const [gameInfo, setGameInfo] = useState<
    RootState["host"]["gamelist"][string] | null
  >(null);

  const [modalDets, setModalDets] = useState({
    open: false,
    status: "",
  });

  const params = useParams<{ gameId: string }>();

  useEffect(() => {
    if (params.gameId && gamelist[params.gameId]) {
      setGameInfo(gamelist[params.gameId]);
    } else if (params.gameId && !gamelist[params.gameId]) {
      // Game doesn't exist in current state, reset to null
      setGameInfo(null);
    }
  }, [gamelist, params.gameId]);

  const startGame = () => {
    if (!gameInfo) return;

    if (Object.keys(gameInfo.waitlist).length >= gameInfo.minPlayers) {
      setModalDets({
        ...modalDets,
        open: true,
        status: "startGame",
      });
    } else {
      setModalDets({
        ...modalDets,
        open: true,
        status: "error",
      });
      console.log(`Need at least ${gameInfo.minPlayers}players to start`);
    }
  };

  const delayGame = () => {
    setModalDets({
      ...modalDets,
      open: true,
      status: "delayGame",
    });
  };

  const endGame = () => {
    setModalDets({
      ...modalDets,
      open: true,
      status: "endGame",
    });
  };

  const renderStartEndGame = () => {
    if (!gameInfo) return null;

    switch (gameInfo.status) {
      case "Interest":
        return (
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<StartIcon />}
              onClick={startGame}
              size="large"
              sx={{
                fontWeight: 500,
                px: 3,
              }}
            >
              Start Game
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<DelayIcon />}
              onClick={delayGame}
              size="large"
              sx={{
                fontWeight: 500,
                px: 3,
              }}
            >
              Delay Game
            </Button>
          </Stack>
        );
      case "Delayed":
        return (
          <Button
            variant="contained"
            color="success"
            startIcon={<StartIcon />}
            onClick={startGame}
            size="large"
            sx={{
              fontWeight: 600,
              px: 3,
              mb: 2,
            }}
          >
            Start Game
          </Button>
        );
      // Running
      default:
        return (
          <Button
            variant="contained"
            color="error"
            startIcon={<EndIcon />}
            onClick={endGame}
            size="large"
            sx={{
              fontWeight: 600,
              px: 3,
              mb: 2,
            }}
          >
            End Game
          </Button>
        );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Interest":
        return "info";
      case "Running":
        return "success";
      case "Delayed":
        return "warning";
      case "Completed":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <ManageGameModal dets={modalDets} gameInfo={gameInfo} />
      {gameInfo ? (
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <CasinoIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4" component="h1" fontWeight={500}>
                  Manage Game
                </Typography>
              </Box>
              
              <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
                {gameInfo.gameName}
              </Typography>

              {/* Access Code for Private Games */}
              {gameInfo.access === "Private" && gameInfo.accesscode && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Access Code
                  </Typography>
                  <Chip
                    label={gameInfo.accesscode}
                    color="secondary"
                    variant="outlined"
                    size="large"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      px: 2,
                      py: 1,
                      bgcolor: "secondary.50",
                      border: "2px solid",
                      borderColor: "secondary.main",
                    }}
                  />
                </Box>
              )}

              {/* Game Status and Stats */}
              <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mb: 4, justifyContent: "center", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Status:
                  </Typography>
                  <Chip
                    label={gameInfo.status}
                    color={getStatusColor(gameInfo.status)}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                
                <Typography variant="body1">
                  <strong>Players:</strong> {gameInfo.minPlayers} - {gameInfo.maxPlayers}
                </Typography>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" component="span">
                    <strong>Waitlist:</strong>
                  </Typography>
                  <Chip
                    label={Object.keys(gameInfo.waitlist).length}
                    color="warning"
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" component="span">
                    <strong>At Table:</strong>
                  </Typography>
                  <Chip
                    label={Object.keys(gameInfo.currentPlayers).length}
                    color="success"
                    size="small"
                  />
                </Box>
              </Stack>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                {renderStartEndGame()}
              </Box>
            </Box>
          </Paper>

          {/* Game Management Sections */}
          <RequestsSection gameInfo={gameInfo} />
          
          <Divider />
          
          <AllowedPlayersSection gameInfo={gameInfo} />
          
          <Divider />
          
          <WaitlistSection gameInfo={gameInfo} />
          
          <Divider />
          
          <CurrentPlayersSection gameInfo={gameInfo} />
        </Stack>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      )}
    </Container>
  );
}

export default ManageGame;
